import { useGuideUrl } from '@/hooks/useGuideUrl'
import { useIntroDialogs, type IntroDialog } from '@/stores/tutorials'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components'
import { useCallback, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HStack } from './Atoms'

type IntroductionDialogProps = {
  dialogId: IntroDialog
  title: string
  children: string
  guideUrl?: string
}

export function IntroductionDialog(props: IntroductionDialogProps) {
  const { t } = useTranslation('common')
  const guideUrl = useGuideUrl(props.guideUrl ?? '')
  const { shouldShowIntroDialog, disableIntroDialog, dismissAllIntroDialogs } = useIntroDialogs(
    props.dialogId,
  )
  const [canShow, setCanShow] = useState(false)

  useLayoutEffect(() => {
    // Wait 1 second before allowing dialog to show
    const timer = setTimeout(() => {
      setCanShow(true)
    }, 175)

    return () => clearTimeout(timer)
  }, [])

  const handleClose = useCallback(() => {
    disableIntroDialog()
  }, [disableIntroDialog])

  const handleCloseAll = useCallback(() => {
    dismissAllIntroDialogs()
  }, [dismissAllIntroDialogs])

  const shouldShow = shouldShowIntroDialog() && canShow

  return (
    <Dialog open={shouldShow} onOpenChange={handleClose}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[85vh] grid-rows-[minmax(0,1fr)_auto]"
      >
        <DialogHeader className="min-h-0 items-center space-y-8 pb-20 pt-16">
          <DialogTitle className="shrink-0 text-3xl">{props.title}</DialogTitle>
          <DialogDescription className="min-h-0 flex-1 overflow-y-auto whitespace-pre-line text-pretty px-4 text-base">
            {props.children}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex w-full sm:items-center sm:justify-between">
          <Button
            tabIndex={3}
            size="sm"
            variant="ghost-destructive"
            className="text-muted-foreground"
            onClick={handleCloseAll}
          >
            {t('tutorials.dialog.dismissAll')}
          </Button>

          <HStack gap="sm">
            {guideUrl && (
              <Button variant="secondary" asChild tabIndex={2}>
                <a href={guideUrl} target="_blank">
                  {t('tutorials.dialog.viewGuide')}
                </a>
              </Button>
            )}

            <Button variant="default" onClick={handleClose} tabIndex={1}>
              {t('tutorials.dialog.gotIt')}
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
